import "./tianaiCaptcha.css";
import {
  TAC_BOTTOM_GAP_PX,
  TAC_DIALOG_HEIGHT_PX,
  TAC_DIALOG_WIDTH_PX,
  TAC_LOGO_HEIGHT_PX,
  TAC_LOGO_WIDTH_PX,
  TAC_LOGO_CENTER_OFFSET_PX,
  TAC_SLIDER_BOTTOM_MARGIN_TOP_PX,
  TAC_SLIDER_BTN_COLOR,
  TAC_SLIDER_HEIGHT_PX,
  TAC_SLIDER_TRACK_BORDER_COLOR,
  TAC_SLIDER_TRACK_FILL_COLOR,
} from "./tianaiCaptchaConfig";

declare global {
  interface Window {
    loadTAC?: (
      tacBase: string,
      config: Record<string, unknown>,
      style?: Record<string, unknown>,
    ) => Promise<{
      init: () => void;
      destroyWindow: () => void;
      reloadCaptcha: () => void;
    }>;
  }
}

const LOAD_SCRIPT_ID = "tac-load-script";
let tacAssetsPreloaded = false;
let captchaSliderObserver: MutationObserver | null = null;

function preloadTacAssets(): void {
  if (tacAssetsPreloaded) {
    return;
  }
  tacAssetsPreloaded = true;
  void ensureLoadScript().then(() => {
    const base = tacPublicBase();
    const cssHref = `${base}/css/tac.css`;
    if (!document.querySelector(`link[href="${cssHref}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = cssHref;
      document.head.appendChild(link);
    }
    const scriptSrc = `${base}/js/tac.min.js`;
    if (!document.querySelector(`script[src="${scriptSrc}"]`)) {
      const script = document.createElement("script");
      script.src = scriptSrc;
      script.async = true;
      document.head.appendChild(script);
    }
  });
}

function tacPublicBase(): string {
  const pub = process.env.PUBLIC_URL ?? "";
  const base = pub.endsWith("/") ? pub.slice(0, -1) : pub;
  return `${window.location.origin}${base}/tac`;
}

function tacLogoUrl(): string {
  const pub = process.env.PUBLIC_URL ?? "";
  const base = pub.endsWith("/") ? pub.slice(0, -1) : pub;
  return `${base}/logo.png`;
}

function ensureLoadScript(): Promise<void> {
  if (window.loadTAC) {
    return Promise.resolve();
  }
  const existing = document.getElementById(LOAD_SCRIPT_ID) as HTMLScriptElement | null;
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("TAC 脚本加载失败")), {
        once: true,
      });
    });
  }
  return new Promise((resolve, reject) => {
    const pub = process.env.PUBLIC_URL ?? "";
    const base = pub.endsWith("/") ? pub.slice(0, -1) : pub;
    const script = document.createElement("script");
    script.id = LOAD_SCRIPT_ID;
    script.src = `${base}/tac/js/load.min.js`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("TAC 脚本加载失败"));
    document.head.appendChild(script);
  });
}

function ensureCaptchaHost(): HTMLElement {
  let host = document.getElementById("tac-captcha-host");
  if (!host) {
    host = document.createElement("div");
    host.id = "tac-captcha-host";
    document.body.appendChild(host);
  }
  ensureCaptchaSliderObserver(host);
  return host;
}

function ensureCaptchaSliderObserver(host: HTMLElement): void {
  if (captchaSliderObserver) {
    return;
  }
  captchaSliderObserver = new MutationObserver(() => {
    const slider = host.querySelector(".tianai-captcha-slider");
    if (!slider) {
      host.classList.remove("tac-captcha-ready");
      return;
    }
    applyCaptchaDomStyles();
    host.classList.add("tac-captcha-ready");
  });
  captchaSliderObserver.observe(host, { childList: true, subtree: true });
}

const SLIDER_TIP_HEIGHT = 25;
const SLIDER_MOVE_MARGIN_TOP = 11;
const SLIDER_BOTTOM_BTN_HEIGHT = 20;
const CONTENT_WIDTH_INSET = 18;
const DEFAULT_CONTENT_WIDTH = 300;
const DEFAULT_CONTENT_HEIGHT = 180;

function resolveContentWidth(): number {
  return TAC_DIALOG_WIDTH_PX - CONTENT_WIDTH_INSET;
}

function resolveContentHeight(): number {
  return Math.round((resolveContentWidth() * DEFAULT_CONTENT_HEIGHT) / DEFAULT_CONTENT_WIDTH);
}

function resolveBottomBarHeight(): number {
  return Math.max(TAC_LOGO_HEIGHT_PX, SLIDER_BOTTOM_BTN_HEIGHT);
}

const PARENT_PADDING = 16;

function resolveSliderMetrics(): {
  btnHeight: number;
  btnTop: number;
  moveHeight: number;
} {
  const btnHeight = Math.round((TAC_SLIDER_HEIGHT_PX * 45) / 32);
  const btnTop = -Math.ceil((btnHeight - TAC_SLIDER_HEIGHT_PX) / 2);
  const moveHeight = Math.max(TAC_SLIDER_HEIGHT_PX + 2, btnHeight + Math.max(0, -btnTop));
  return { btnHeight, btnTop, moveHeight };
}

function resolveSliderLayout(): {
  btnHeight: number;
  btnTop: number;
  moveHeight: number;
  moveMarginBottom: number;
  captchaHeight: number;
  boxHeight: number;
  parentHeight: number;
} {
  const { btnHeight, btnTop, moveHeight } = resolveSliderMetrics();
  const contentHeight = resolveContentHeight();
  const moveMarginBottom = Math.max(2, 11 + TAC_BOTTOM_GAP_PX);
  const captchaHeight =
    SLIDER_TIP_HEIGHT +
    contentHeight +
    SLIDER_MOVE_MARGIN_TOP +
    moveHeight +
    moveMarginBottom;
  const boxHeight = captchaHeight;
  const autoParentHeight =
    PARENT_PADDING +
    boxHeight +
    resolveBottomBarHeight() +
    TAC_SLIDER_BOTTOM_MARGIN_TOP_PX +
    Math.max(0, TAC_BOTTOM_GAP_PX);
  const parentHeight = TAC_DIALOG_HEIGHT_PX > 0 ? TAC_DIALOG_HEIGHT_PX : autoParentHeight;
  return { btnHeight, btnTop, moveHeight, moveMarginBottom, captchaHeight, boxHeight, parentHeight };
}

function applyCaptchaTheme(host: HTMLElement): void {
  const { btnHeight, btnTop, moveHeight } = resolveSliderMetrics();
  host.style.setProperty("--tac-bottom-gap", `${TAC_BOTTOM_GAP_PX}px`);
  host.style.setProperty("--tac-slider-border-color", TAC_SLIDER_TRACK_BORDER_COLOR);
  host.style.setProperty("--tac-slider-fill-color", TAC_SLIDER_TRACK_FILL_COLOR);
  host.style.setProperty("--tac-slider-btn-color", TAC_SLIDER_BTN_COLOR);
  host.style.setProperty("--tac-slider-height", `${TAC_SLIDER_HEIGHT_PX}px`);
  host.style.setProperty("--tac-slider-move-height", `${moveHeight}px`);
  host.style.setProperty("--tac-slider-btn-height", `${btnHeight}px`);
  host.style.setProperty("--tac-slider-btn-top", `${btnTop}px`);
  host.style.setProperty("--tac-logo-height", `${TAC_LOGO_HEIGHT_PX}px`);
  host.style.setProperty(
    "--tac-logo-width",
    TAC_LOGO_WIDTH_PX > 0 ? `${TAC_LOGO_WIDTH_PX}px` : "auto",
  );
  host.style.setProperty("--tac-logo-center-offset", `${TAC_LOGO_CENTER_OFFSET_PX}px`);
  host.style.setProperty("--tac-slider-bottom-margin-top", `${TAC_SLIDER_BOTTOM_MARGIN_TOP_PX}px`);
  host.style.setProperty("--tac-slider-bottom-height", `${resolveBottomBarHeight()}px`);
  host.style.setProperty("--tac-dialog-width", `${TAC_DIALOG_WIDTH_PX}px`);
  host.style.setProperty(
    "--tac-dialog-height",
    TAC_DIALOG_HEIGHT_PX > 0 ? `${TAC_DIALOG_HEIGHT_PX}px` : "auto",
  );
  host.style.setProperty("--tac-content-width", `${resolveContentWidth()}px`);
  host.style.setProperty("--tac-content-height", `${resolveContentHeight()}px`);
}

function applyDialogSizeStyles(host: HTMLElement, important: (el: HTMLElement | null, key: string, value: string) => void): void {
  const parent = host.querySelector<HTMLElement>("#tianai-captcha-parent");
  const captcha = host.querySelector<HTMLElement>("#tianai-captcha");
  const contentWidth = `${resolveContentWidth()}px`;
  const contentHeight = `${resolveContentHeight()}px`;
  important(parent, "width", `${TAC_DIALOG_WIDTH_PX}px`);
  important(captcha, "width", contentWidth);
  host
    .querySelectorAll<HTMLElement>(
      ".tianai-captcha-slider .content, .tianai-captcha-rotate .content, .tianai-captcha-disable .content",
    )
    .forEach((el) => {
      important(el, "height", contentHeight);
    });
  host.querySelectorAll<HTMLElement>(".tianai-captcha-concat .tianai-captcha-slider-concat-bg-img").forEach((el) => {
    important(el, "background-size", `100% ${contentHeight}`);
  });
  host.querySelectorAll<HTMLElement>(".tianai-captcha-concat .tianai-captcha-slider-concat-img-div").forEach((el) => {
    important(el, "background-size", `100% ${contentHeight}`);
  });
  if (TAC_DIALOG_HEIGHT_PX > 0) {
    important(parent, "height", `${TAC_DIALOG_HEIGHT_PX}px`);
  }
}

function applySliderBottomStyles(host: HTMLElement, important: (el: HTMLElement | null, key: string, value: string) => void): void {
  host.querySelectorAll<HTMLElement>("#tianai-captcha-parent > .slider-bottom").forEach((el) => {
    important(el, "display", "flex");
    important(el, "align-items", "center");
    important(el, "margin-top", `${TAC_SLIDER_BOTTOM_MARGIN_TOP_PX}px`);
    important(el, "height", `${resolveBottomBarHeight()}px`);
  });
  host.querySelectorAll<HTMLElement>("#tianai-captcha-logo").forEach((el) => {
    important(el, "height", `${TAC_LOGO_HEIGHT_PX}px`);
    important(el, "transform", `translateY(${TAC_LOGO_CENTER_OFFSET_PX}px)`);
    if (TAC_LOGO_WIDTH_PX > 0) {
      important(el, "width", `${TAC_LOGO_WIDTH_PX}px`);
    } else {
      el.style.removeProperty("width");
    }
  });
}

function applyCaptchaDomStyles(): void {
  const host = document.getElementById("tac-captcha-host");
  if (!host) {
    return;
  }
  applyCaptchaTheme(host);
  const important = (el: HTMLElement | null, key: string, value: string) => {
    el?.style.setProperty(key, value, "important");
  };
  const clear = (el: HTMLElement | null, key: string) => {
    el?.style.removeProperty(key);
  };
  const parent = host.querySelector<HTMLElement>("#tianai-captcha-parent");
  const box = host.querySelector<HTMLElement>("#tianai-captcha-box");
  const captcha = host.querySelector<HTMLElement>("#tianai-captcha");
  const isSlider = host.querySelector(".tianai-captcha-slider");
  applyDialogSizeStyles(host, important);
  applySliderBottomStyles(host, important);

  if (isSlider) {
    const layout = resolveSliderLayout();
    const { btnHeight, btnTop, moveHeight, moveMarginBottom, captchaHeight, boxHeight, parentHeight } =
      layout;
    important(parent, "height", `${parentHeight}px`);
    important(box, "height", `${boxHeight}px`);
    important(parent, "overflow", "hidden");
    important(box, "overflow", "hidden");
    important(captcha, "height", `${captchaHeight}px`);
    important(captcha, "overflow", "visible");
    host.querySelectorAll<HTMLElement>(".tianai-captcha-slider .slider-move-track").forEach((el) => {
      important(el, "height", `${TAC_SLIDER_HEIGHT_PX}px`);
      important(el, "line-height", `${TAC_SLIDER_HEIGHT_PX}px`);
    });
    important(host.querySelector("#tianai-captcha-slider-move-track-mask"), "height", `${TAC_SLIDER_HEIGHT_PX}px`);
    host.querySelectorAll<HTMLElement>(".tianai-captcha-slider .slider-move").forEach((el) => {
      important(el, "height", `${moveHeight}px`);
      important(el, "overflow", "visible");
      important(el, "margin-bottom", `${moveMarginBottom}px`);
    });
    host.querySelectorAll<HTMLElement>(".tianai-captcha-slider .slider-move .slider-move-btn").forEach((el) => {
      important(el, "height", `${btnHeight}px`);
      important(el, "top", `${btnTop}px`);
      important(el, "background-image", "none");
      important(el, "background-color", TAC_SLIDER_BTN_COLOR);
    });
  } else {
    important(parent, "height", "auto");
    important(parent, "overflow", "visible");
    important(box, "height", "auto");
    important(box, "overflow", "visible");
    important(captcha, "height", "auto");
  }
}

function scheduleApplyCaptchaDomStyles(): void {
  applyCaptchaDomStyles();
  window.requestAnimationFrame(applyCaptchaDomStyles);
  window.setTimeout(applyCaptchaDomStyles, 80);
  window.setTimeout(applyCaptchaDomStyles, 300);
}

function showCaptchaHost(): void {
  const host = ensureCaptchaHost();
  applyCaptchaTheme(host);
  host.classList.remove("tac-captcha-ready");
  host.classList.add("tac-captcha-host--open");
}

function hideCaptchaHost(): void {
  const host = document.getElementById("tac-captcha-host");
  host?.classList.remove("tac-captcha-host--open");
  host?.classList.remove("tac-captcha-ready");
}

export type OpenLoginCaptchaOptions = {
  genUrl: string;
  checkUrl: string;
  onSuccess: (token: string) => void | Promise<void>;
  onFail?: () => void;
};

/** 弹出 TIANAI-CAPTCHA 滑块；校验成功后回调 token（二次验证凭证） */
export async function openLoginCaptcha(opts: OpenLoginCaptchaOptions): Promise<void> {
  await ensureLoadScript();
  if (!window.loadTAC) {
    throw new Error("TAC 未就绪");
  }
  showCaptchaHost();

  try {
    await window
      .loadTAC(
        tacPublicBase(),
        {
          requestCaptchaDataUrl: opts.genUrl,
          validCaptchaUrl: opts.checkUrl,
          bindEl: "#tac-captcha-host",
          validSuccess: (
            res: { data?: { token?: string } },
            _c: unknown,
            tac: { destroyWindow: () => void },
          ) => {
            tac.destroyWindow();
            hideCaptchaHost();
            const token = res?.data?.token;
            if (!token) {
              opts.onFail?.();
              return;
            }
            void Promise.resolve(opts.onSuccess(token));
          },
          validFail: (_res: unknown, _c: unknown, tac: { reloadCaptcha: () => void }) => {
            tac.reloadCaptcha();
            scheduleApplyCaptchaDomStyles();
          },
          btnRefreshFun: (_el: unknown, tac: { reloadCaptcha: () => void }) => {
            tac.reloadCaptcha();
            scheduleApplyCaptchaDomStyles();
          },
          btnCloseFun: (_el: unknown, tac: { destroyWindow: () => void }) => {
            tac.destroyWindow();
            hideCaptchaHost();
          },
        },
        {
          logoUrl: tacLogoUrl(),
          moveTrackMaskBorderColor: TAC_SLIDER_TRACK_BORDER_COLOR,
          moveTrackMaskBgColor: TAC_SLIDER_TRACK_FILL_COLOR,
        },
      )
      .then((tac) => {
        tac.init();
        scheduleApplyCaptchaDomStyles();
        return tac;
      });
  } catch (e) {
    hideCaptchaHost();
    throw e;
  }
}

preloadTacAssets();
